import { LitElement, html, nothing, type TemplateResult, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';

export type MessageSeverity = 'info' | 'success' | 'warn' | 'error' | 'secondary' | 'contrast';
export type MessageVariant  = 'filled' | 'outlined' | 'simple';

/**
 * uwc-message
 *
 * Inline alert banner with filled, outlined, and simple variants.
 *
 * @attr severity    - info | success | warn | error | secondary | contrast. Default: info.
 * @attr text        - Summary / title text.
 * @attr detail      - Secondary body text shown below the summary.
 * @attr icon        - Override the default severity icon (Bootstrap Icons name).
 * @attr closable    - Show a dismiss button.
 * @attr variant     - filled | outlined | simple. Default: filled.
 *
 * @prop contentTemplate - (optional) () => TemplateResult — replaces default text/detail rendering.
 *
 * @fires uwc-close  - Fired when the message is dismissed.
 *
 * @slot             - Default slot: replaces text + detail area (lowest precedence).
 */
export class UwcMessage extends LitElement {
  static override styles: CSSResultGroup = styles;

  @property() severity: MessageSeverity = 'info';
  @property() text = '';
  @property() detail = '';
  @property() icon?: string;
  @property({ type: Boolean }) closable = false;
  @property() variant: MessageVariant = 'filled';

  contentTemplate?: () => TemplateResult;

  @state() private _closing = false;
  @state() private _closed  = false;

  private static readonly _ICONS: Record<MessageSeverity, string> = {
    info:      'info-circle-fill',
    success:   'check-circle-fill',
    warn:      'exclamation-triangle-fill',
    error:     'x-circle-fill',
    secondary: 'info-circle',
    contrast:  'info-circle-fill',
  };

  private _getIcon() {
    return this.icon ?? UwcMessage._ICONS[this.severity] ?? 'info-circle-fill';
  }

  private _dismiss() {
    this._closing = true;
    const el = this.renderRoot.querySelector('.uwc-msg');
    const done = () => {
      this._closed = true;
      emit(this, 'uwc-close', {});
    };
    if (el) {
      el.addEventListener('animationend', done, { once: true });
    } else {
      done();
    }
  }

  override render() {
    if (this._closed) return nothing;

    const classes = {
      'uwc-msg': true,
      [`uwc-msg--${this.severity}`]: true,
      [`uwc-msg--${this.variant}`]:  true,
      'uwc-msg--closing': this._closing,
    };

    return html`
      <div
        class=${classMap(classes)}
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <span class="uwc-msg__icon-wrap" aria-hidden="true">
          <uwc-icon name=${this._getIcon()} class="uwc-msg__icon"></uwc-icon>
        </span>

        <div class="uwc-msg__body">
          ${this.contentTemplate
            ? this.contentTemplate()
            : html`
              ${this.text   ? html`<span class="uwc-msg__text">${this.text}</span>`     : nothing}
              ${this.detail ? html`<span class="uwc-msg__detail">${this.detail}</span>` : nothing}
              <slot></slot>
            `}
        </div>

        ${this.closable ? html`
          <button
            type="button"
            class="uwc-msg__close"
            aria-label="Dismiss message"
            @click=${this._dismiss}
          >
            <uwc-icon name="x-lg"></uwc-icon>
          </button>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-message': UwcMessage; }
}
